import { useSyncExternalStore } from "react";
import { Scheme } from "../app/appearance.ts";

// The reader's Aa settings, kept in this browser like the app keeps them on the phone
// (apps/ios/DesignSystem/ReaderLook.swift). The page has its own colours, apart from the stash's
// walnut and parchment, so the reading's the thing.

export type ReaderPageChoice = "app" | "paper" | "white" | "dark";
export type ReaderWidth = "narrow" | "medium" | "wide";

export interface ReaderSettings {
  /** An index into `textSteps` */
  step: number;
  sans: boolean;
  page: ReaderPageChoice;
  width: ReaderWidth;
}

/** The Aa sizes, as multiples of the reading size. */
export const textSteps = [0.82, 0.9, 1, 1.1, 1.22, 1.36, 1.52];

const defaults: ReaderSettings = { step: 2, sans: false, page: "app", width: "medium" };
const key = "stashit.reader";

export interface ReaderColors {
  page: string;
  ink: string;
  ink2: string;
  link: string;
  isDark: boolean;
}

/** Body text is 13:1 or better on its page, quieter text and links 5.4:1 or better. */
const pages: Record<Exclude<ReaderPageChoice, "app">, ReaderColors> = {
  paper: { page: "#faf6ee", ink: "#2a251f", ink2: "#6b6257", link: "#8f4a1e", isDark: false },
  white: { page: "#ffffff", ink: "#1e1e1e", ink2: "#6a6a6a", link: "#8f4a1e", isDark: false },
  dark: { page: "#1a1917", ink: "#e4ded3", ink2: "#a29a8e", link: "#dda36f", isDark: true },
};

/** "Match the app" is the dark page when the app's dark, and paper when it isn't. */
export const readerColors = (choice: ReaderPageChoice, appScheme: Scheme): ReaderColors =>
  pages[choice == "app" ? (appScheme == "dark" ? "dark" : "paper") : choice];

const read = (): ReaderSettings => {
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "{}");
    return { ...defaults, ...stored };
  } catch {
    return defaults;
  }
};

let settings = read();
const listeners = new Set<() => void>();

export const updateReaderSettings = (change: Partial<ReaderSettings>) => {
  settings = { ...settings, ...change };
  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    // Still applies until the tab closes
  }
  listeners.forEach((listener) => listener());
};

export const useReaderSettings = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => settings,
  );
